import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  addPackage,
  addPackageSupply,
  dispatchOrder,
  fetchFulfillment,
  fetchPackingOptions,
  labelDelivery,
  pickLine,
  removePackage,
  removePackageSupply,
  reserveOrder,
  savePackage,
  shortPickLine,
  unpickLine,
} from './fulfillmentThunks';
import { notificationReceived } from '../notifications/notificationsSlice';
import { parseChannel } from '../notifications/notificationsUtils';
import { NOTIFICATION_KIND } from '../notifications/notificationsConstants';

const initialState = {
  /** The order being prepared, as `wms/orders/<id>/` answers it. */
  order: null,
  /** The lines to pick, each with its suggestions, in route order. */
  lines: [],
  /** The parcels of the shipment, in index order. */
  packages: [],
  /** The packing supplies of the order's warehouse, with what is left of each. */
  supplyOptions: [],
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchFulfillment.fulfilled`. */
  requestId: null,

  /** The act in flight on the order as a whole: 'reserve', 'label', 'dispatch', 'addPackage'. */
  busyAction: null,
  /** The `order_product_id` being picked, unpicked or reported. */
  busyLineId: null,
  /** The parcel being saved or removed. */
  busyPackageId: null,
  /**
   * Etiquetar was accepted and the courier has not answered yet. The answer
   * comes over the socket; see `notificationReceived` below.
   */
  labelWaiting: false,
  /** The courier's answer for this order, until the screen dismisses it. */
  labelResult: null,
};

const orderActions = {
  [reserveOrder.typePrefix]: 'reserve',
  [labelDelivery.typePrefix]: 'label',
  [dispatchOrder.typePrefix]: 'dispatch',
  [addPackage.typePrefix]: 'addPackage',
};

const fulfillmentSlice = createSlice({
  name: 'fulfillment',
  initialState,
  reducers: {
    /** Leaving the screen forgets the order, so the next one never shows under it. */
    clearFulfillment: () => initialState,
    /** The dialog with the courier's answer was closed. */
    labelResultDismissed: (state) => {
      state.labelResult = null;
    },
    /** The socket stayed silent past `LABEL_WAIT_MS`: stop waiting. */
    labelWaitGaveUp: (state) => {
      state.labelWaiting = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFulfillment.pending, (state, action) => {
        state.loading = state.order === null || String(state.order.id) !== String(action.meta.arg);
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchFulfillment.fulfilled, (state, action) => {
        // Only the latest request may write: every act reloads, and two acts
        // in a row would otherwise let the older answer land last.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.order = action.payload.order;
        state.lines = action.payload.lines;
        state.packages = action.payload.packages;
        state.supplyOptions = action.payload.supplyOptions;
      })
      .addCase(fetchPackingOptions.fulfilled, (state, action) => {
        state.supplyOptions = action.payload;
      })
      .addCase(fetchFulfillment.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
      })

      // The label request only queues the courier call; from here on the
      // socket does the talking (`label_{role}_{user}`, see the task).
      .addCase(labelDelivery.fulfilled, (state) => {
        state.labelWaiting = true;
        state.labelResult = null;
      })
      .addCase(notificationReceived, (state, action) => {
        const { channel, payload, role, userId } = action.payload;
        if (parseChannel(channel, role, userId) !== NOTIFICATION_KIND.LABEL) return;
        // Only this order's: a second screen, or an answer that arrives after
        // moving on to the next order, is not news here.
        if (!state.order || String(payload?.order_id) !== String(state.order.id)) return;
        state.labelWaiting = false;
        state.labelResult = payload;
      })

      .addCase(savePackage.pending, (state, action) => {
        state.busyPackageId = action.meta.arg.packageId;
      })
      .addCase(savePackage.fulfilled, (state, action) => {
        state.busyPackageId = null;
        // The answer is the parcel as it is now; the screen takes it without
        // asking for the whole list again.
        state.packages = state.packages.map((pkg) =>
          pkg.id === action.payload.id ? { ...pkg, ...action.payload } : pkg
        );
      })
      .addCase(savePackage.rejected, (state) => {
        state.busyPackageId = null;
      })

      .addCase(removePackage.pending, (state, action) => {
        state.busyPackageId = action.meta.arg.packageId;
      })
      .addMatcher(isAnyOf(removePackage.fulfilled, removePackage.rejected), (state) => {
        state.busyPackageId = null;
      })

      // Using or putting back a supply answers with the parcel as it is now.
      .addMatcher(isAnyOf(addPackageSupply.pending, removePackageSupply.pending), (state, action) => {
        state.busyPackageId = action.meta.arg.packageId;
      })
      .addMatcher(isAnyOf(addPackageSupply.fulfilled, removePackageSupply.fulfilled), (state, action) => {
        state.busyPackageId = null;
        state.packages = state.packages.map((pkg) =>
          pkg.id === action.payload.id ? { ...pkg, ...action.payload } : pkg
        );
      })
      .addMatcher(isAnyOf(addPackageSupply.rejected, removePackageSupply.rejected), (state) => {
        state.busyPackageId = null;
      })

      .addMatcher(
        isAnyOf(pickLine.pending, shortPickLine.pending, unpickLine.pending),
        (state, action) => {
          state.busyLineId = action.meta.arg.line.order_product_id;
        }
      )
      .addMatcher(
        isAnyOf(
          pickLine.fulfilled,
          pickLine.rejected,
          shortPickLine.fulfilled,
          shortPickLine.rejected,
          unpickLine.fulfilled,
          unpickLine.rejected
        ),
        (state) => {
          state.busyLineId = null;
        }
      )

      .addMatcher(
        isAnyOf(reserveOrder.pending, labelDelivery.pending, dispatchOrder.pending, addPackage.pending),
        (state, action) => {
          state.busyAction = orderActions[action.type.replace(/\/pending$/, '')];
        }
      )
      .addMatcher(
        isAnyOf(
          reserveOrder.fulfilled,
          reserveOrder.rejected,
          labelDelivery.fulfilled,
          labelDelivery.rejected,
          dispatchOrder.fulfilled,
          dispatchOrder.rejected,
          addPackage.fulfilled,
          addPackage.rejected
        ),
        (state) => {
          state.busyAction = null;
        }
      );
  },
});

export const { clearFulfillment, labelResultDismissed, labelWaitGaveUp } = fulfillmentSlice.actions;

export default fulfillmentSlice.reducer;
